import { useEnvironmentVariables } from "#app/EnvironmentProvider.tsx";
import { useAssistantVariant } from "#contexts/AssistantVariantContext.ts";
import { ComposerEmojiMenu } from "#features/conversations/components/conversation-input/ComposerEmojiMenu.tsx";
import { ComposerMentionTray } from "#features/conversations/components/conversation-input/ComposerMentionTray.tsx";
import type { ConversationInputProps } from "#features/conversations/components/conversation-input/composerEditor.ts";
import type { EmojiSuggestion } from "#features/conversations/components/conversation-input/emojiSuggestion.ts";
import { LazyConversationTiptapInput } from "#features/conversations/components/conversation-input/LazyConversationTiptapInput.ts";
import type { EditorListbox, MentionCandidate, MentionSuggestion } from "#features/conversations/components/conversation-input/mentionSuggestion.ts";
import { useMemberDisplays } from "#features/conversations/hooks/useMemberDisplays.ts";
import { useSkillDisplays } from "#features/conversations/hooks/useSkillDisplays.ts";
import { useMemberDisplayStore } from "#features/conversations/stores/useMemberDisplayStore.ts";
import { mentionedMemberIds } from "#features/conversations/utils/composerDocument.ts";
import { useFeatureFlag } from "#hooks/useFeatureFlag.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useAiApiClient } from "#lib/AiApiClient.ts";
import { useGetCurrentUserSuspenseQuery } from "#lib/useGetCurrentUserSuspenseQuery.ts";
import { useAssistantOptionsStore } from "#stores/useAssistantOptionsStore.ts";
import { IconPeople } from "@intentui/icons";
import { useCallback, useEffect, useId, useMemo, useState, type RefObject } from "react";

const MENTION_CANDIDATE_COUNT = 5;
// A search that answers sooner fills the tray without a spinner flashing first.
const MENTION_LOADING_DELAY_MS = 150;
// wl-app's admin page of the organization's members.
const WL_APP_PEOPLE_PATH = "/settings/people";

interface Props extends ConversationInputProps {
    // The bar the mention tray opens above, as the "/" skill tray does.
    trayAnchorRef: RefObject<HTMLElement | null>;
    // The open "/" skill tray, from the menu that draws it.
    slashListbox: EditorListbox | null;
}

// Stays in the main bundle and hands the lazy editor plain values, so the editor's chunk imports no app hooks.
export const TiptapComposerEditor = ({ trayAnchorRef, slashListbox, ...props }: Props) => {
    const { tShared } = useSharedTranslation();
    const { wlAppBaseUrl } = useEnvironmentVariables();
    const { data: { isAdmin } } = useGetCurrentUserSuspenseQuery();
    // Only the unified assistant, inside wl-app, has wl-app's people page to link to.
    const linksToWlApp = useAssistantVariant() === "unified";
    const inputPlaceholder = useAssistantOptionsStore(state => state.options.inputPlaceholder);
    const { hasFeatureFlag } = useFeatureFlag();
    const isSlashMenuEnabled = hasFeatureFlag("SlashCommandPicker");
    const skillDisplays = useSkillDisplays();

    // A restored draft can mention members the "@" list hasn't found this session.
    const mentionedIds = useMemo(() => mentionedMemberIds(props.value), [props.value]);
    const memberDisplays = useMemberDisplays(mentionedIds);
    const rememberMembers = useMemberDisplayStore(state => state.rememberMembers);

    const { searchMembers } = useAiApiClient();
    const findMentionCandidates = useCallback(async (query: string, signal?: AbortSignal) => {
        const members = (await searchMembers({ query }, signal)).slice(0, MENTION_CANDIDATE_COUNT).map(({ id, fullName, imageUrl, jobTitle }) => ({
            id,
            fullName,
            imageUrl: imageUrl ?? undefined,
            jobTitle: jobTitle ?? undefined
        }));
        rememberMembers(members);

        return members.map(({ id, fullName, imageUrl, jobTitle }) => ({ id, label: fullName, imageUrl, description: jobTitle }));
    }, [searchMembers, rememberMembers]);
    const [mentionSuggestion, setMentionSuggestion] = useState<MentionSuggestion | null>(null);
    const mentionListboxId = useId();
    const mentionOptionId = useCallback((candidate: MentionCandidate) => `${mentionListboxId}-${candidate.id}`, [mentionListboxId]);
    const isMentionSearchLoading = mentionSuggestion?.isLoading ?? false;
    const [isMentionSearchSlow, setIsMentionSearchSlow] = useState(false);
    useEffect(() => {
        if (!isMentionSearchLoading) {
            return;
        }
        const timer = setTimeout(() => setIsMentionSearchSlow(true), MENTION_LOADING_DELAY_MS);

        return () => {
            clearTimeout(timer);
            setIsMentionSearchSlow(false);
        };
    }, [isMentionSearchLoading]);
    const isMentionTrayOpen = mentionSuggestion !== null && (mentionSuggestion.candidates.length > 0 || (isMentionSearchLoading && isMentionSearchSlow));
    const activeMentionCandidate = mentionSuggestion?.candidates[mentionSuggestion.activeIndex];
    const managePeopleId = `${mentionListboxId}-manage-people`;
    const managePeopleHref = isAdmin && linksToWlApp ? `${wlAppBaseUrl}${WL_APP_PEOPLE_PATH}` : undefined;
    const openManagePeople = useMemo(
        () => (managePeopleHref ? () => window.open(managePeopleHref, "_blank", "noopener") : undefined),
        [managePeopleHref]
    );
    const activeMentionOptionId = activeMentionCandidate
        ? mentionOptionId(activeMentionCandidate)
        : (managePeopleHref && mentionSuggestion?.candidates.length ? managePeopleId : undefined);

    const [emojiSuggestion, setEmojiSuggestion] = useState<EmojiSuggestion | null>(null);
    const emojiListboxId = useId();
    const emojiOptionId = useCallback((index: number) => `${emojiListboxId}-${index}`, [emojiListboxId]);
    // One list at a time: an "@" still open past a ":" ("@nobody :smi") keeps it while it takes the keys, with rows up or its
    // first search still answering, its tray shown or not.
    const isMentionTakingKeys = mentionSuggestion !== null && (mentionSuggestion.candidates.length > 0 || mentionSuggestion.isLoading);
    const isEmojiMenuOpen = !isMentionTakingKeys && emojiSuggestion !== null && emojiSuggestion.matches.length > 0;

    return (
        <>
            <LazyConversationTiptapInput
                {...props}
                placeholder={inputPlaceholder || tShared("conversationInputPlaceholder")}
                isSlashMenuEnabled={isSlashMenuEnabled}
                skillDisplays={skillDisplays}
                memberDisplays={memberDisplays}
                findMentionCandidates={findMentionCandidates}
                onMentionSuggestionChange={setMentionSuggestion}
                onMentionAction={openManagePeople}
                slashListbox={slashListbox}
                mentionListbox={isMentionTrayOpen ? { id: mentionListboxId, activeOptionId: activeMentionOptionId } : null}
                onEmojiSuggestionChange={setEmojiSuggestion}
                emojiListbox={isEmojiMenuOpen ? { id: emojiListboxId, activeOptionId: emojiOptionId(emojiSuggestion.activeIndex) } : null}
            />
            {isEmojiMenuOpen && (
                <ComposerEmojiMenu suggestion={emojiSuggestion} listboxId={emojiListboxId} optionId={emojiOptionId} label={tShared("emojiListLabel")} />
            )}
            {isMentionTrayOpen && (
                <ComposerMentionTray
                    suggestion={mentionSuggestion}
                    anchorRef={trayAnchorRef}
                    listboxId={mentionListboxId}
                    optionId={mentionOptionId}
                    title={tShared("composerTrayPeople")}
                    loadingLabel={tShared("composerTraySearchingPeople")}
                    action={managePeopleHref
                        ? { id: managePeopleId, name: tShared("managePeople"), icon: <IconPeople />, href: managePeopleHref, statusLabel: tShared("opensInNewTab") }
                        : undefined}
                />
            )}
        </>
    );
};
